import { eventBus } from './EventBus.js';

export class MockDataGenerator {
    constructor(deviceManager) {
        this.deviceManager = deviceManager;
        this.devices = deviceManager.devices;
        
        // Configuration
        this.config = {
            batteryDrainPerSecond: 20 / (5 * 60), // 20% over 5 minutes
            batteryFloor: 5, // Stop at 20%
            signalInterval: 3 * 60 * 1000, // 3 minutes in ms
            smokeRange: { min: 0, max: 15 },
            smokeWalkStep: 0.8, // Max change per update
            updateInterval: 2000, // Check every 2 seconds
        };
        
        this.lastUpdate = 0;
        this.active = true;
        this.deviceTimers = new Map(); // Track individual device signal timers
        
        // Calculate offset based on last digit of ID * PI
        this.getSignalOffset = (deviceId) => {
            const lastDigit = parseInt(deviceId.slice(-1)) || 0;
            return lastDigit * Math.PI * 1000; // Convert to milliseconds
        };
        
        // Listen for fire events
        eventBus.on('fire:simulate', () => { 
            this.active = false; 
            console.log('[MockData] Paused for fire simulation');
        });
        
        eventBus.on('fire:reset', () => { 
            this.active = true; 
            console.log('[MockData] Resumed after fire reset');
        });
        
        // Initialize staggered timers for each device
        this.initializeDeviceTimers();
    }
    
    initializeDeviceTimers() {
        this.devices.forEach((device, id) => {
            const offset = this.getSignalOffset(id);
            this.deviceTimers.set(id, {
                lastSignalFlip: performance.now() - this.config.signalInterval + offset,
                nextSmokeChange: Math.random() * 2000 // Randomize first smoke update
            });
        });
    }
    
    update(deltaTime, currentTime) {
        if (!this.active) return;
        if (currentTime - this.lastUpdate < this.config.updateInterval) return;
        
        this.lastUpdate = currentTime;
        
        this.devices.forEach((device, id) => {
            // Skip if device doesn't exist or is offline/alarm/test
            if (!device || device.state === 'offline' || device.state === 'alarm' || device.state === 'test') return;
            
            const timers = this.deviceTimers.get(id);
            if (!timers) return;
            
            // 1. Battery drain (toward 20%, stop there)
            if (device.battery > this.config.batteryFloor) {
                const drainAmount = this.config.batteryDrainPerSecond * (this.config.updateInterval / 1000);
                device.battery = Math.max(this.config.batteryFloor, device.battery - drainAmount);
                device.battery = Math.round(device.battery * 10) / 10; // 1 decimal
            }
            
            // 2. Signal fluctuation (strong ↔ fair) every 3 minutes with PI offset
            if (currentTime - timers.lastSignalFlip >= this.config.signalInterval) {
                // Toggle between strong and fair
                device.signal = device.signal === 'strong' ? 'fair' : 'strong';
                timers.lastSignalFlip = currentTime;
                console.log(`[MockData] ${id} signal changed to ${device.signal}`);
            }
            
            // 3. Ambient smoke random walk (0%–15%) - only if not climbing from fire
            if (!device.smokeClimbActive) {
                const change = (Math.random() - 0.5) * this.config.smokeWalkStep * 2;
                let newSmoke = (device.smoke || 0) + change;
                newSmoke = Math.max(this.config.smokeRange.min, 
                           Math.min(this.config.smokeRange.max, newSmoke));
                device.smoke = Math.round(newSmoke * 10) / 10;
            }
            
            // Emit update to refresh UI
            eventBus.emit('device:updated', {
                id: id,
                state: device.state,
                type: device.type,
                location: device.location,
                signal: device.signal,
                battery: device.battery,
                smoke: device.smoke,
                lastTest: device.lastTest,
                muted: device.muted
            });
        });
    }
}